import SvgClose from '@sats-group/icons/16/close';
import Button from '@sats-group/ui-lib/react/button';
import Text from '@sats-group/ui-lib/react/text';
import { createFocusTrap, type FocusTrap } from 'focus-trap';
import React, { useEffect, useImperativeHandle, useRef } from 'react';
import type { Dialog as Props } from './dialog.types';

export const Dialog: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  close,
  id,
  ref,
  title,
}) => {
  const innerRef = useRef<HTMLDialogElement>(null);

  useImperativeHandle<HTMLDialogElement | null, HTMLDialogElement | null>(
    ref,
    () => innerRef.current,
    []
  );

  const hide = () => {
    if (innerRef.current) {
      innerRef.current.hidePopover();
    }
  };

  const createCloseHandler = (focusTrap: FocusTrap) => (event: ToggleEvent) => {
    switch (event.newState) {
      case 'closed': {
        focusTrap.deactivate();
        break;
      }
    }
  };

  const createOpenHandler = (focusTrap: FocusTrap) => (event: ToggleEvent) => {
    switch (event.newState) {
      case 'open': {
        focusTrap.activate();
        break;
      }
    }
  };

  useEffect(() => {
    if (!innerRef.current) {
      return;
    }

    const focusTrap = createFocusTrap(innerRef.current, {
      escapeDeactivates: () => {
        hide();
        return true;
      },
    });

    const closeHandler = createCloseHandler(focusTrap);
    const openHandler = createOpenHandler(focusTrap);

    // NOTE: tsgo fails with a type error on these handlers, but tsc does not.

    // @ts-ignore
    innerRef.current.addEventListener('beforetoggle', closeHandler);
    // @ts-ignore
    innerRef.current.addEventListener('toggle', openHandler);

    return () => {
      if (innerRef.current) {
        // @ts-ignore
        innerRef.current.removeEventListener('beforetoggle', closeHandler);
        // @ts-ignore
        innerRef.current.removeEventListener('toggle', openHandler);
      }
    };
  }, []);

  return (
    <dialog
      aria-labelledby={`title-for-${id}`}
      aria-modal
      autoFocus
      className="dialog"
      id={id}
      popover="auto"
      ref={innerRef}
    >
      <div className="dialog__click-trapper" onClick={hide} />
      <div className="dialog__inner">
        <div className="dialog__header">
          <div className="dialog__title">
            <Text
              id={`title-for-${id}`}
              size={Text.sizes.headline3}
              theme={Text.themes.emphasis}
            >
              {title}
            </Text>
          </div>
          <div>
            <Button
              iconOnly
              leadingIcon={<SvgClose />}
              popoverTarget={id}
              popoverTargetAction="hide"
              size={Button.sizes.small}
              text={close}
              variant={Button.variants.secondary}
            />
          </div>
        </div>
        <div className="dialog__children">{children}</div>
      </div>
    </dialog>
  );
};
